<template>
  <div class="w-full rounded-md border border-light-gray p-6">
    <div class="flex w-full items-center gap-6">
      <h3 class="text-base text-dark-gray lg:text-xl">Authority</h3>
      <div class="flex items-center">
        <custom-badge
          v-if="emptyElement(authority?.authorities)"
          bg-color-class="bg-light-gray"
          color-class="text-body-color"
          size="md"
          text="No Authority"
        />
      </div>
    </div>
    <div
      v-if="!emptyElement(authority?.authorities)"
      class="mt-4 flex flex-wrap justify-between gap-4"
      :class="{ 'flex-col': screenSize == 'mobile' }"
    >
      <div
        class="flex-auto"
        :class="{ 'w-full': screenSize == 'mobile' }"
      >
        <div
          class="flex items-start gap-2 rounded-md border border-light-gray p-3 md:gap-8 md:p-8"
          :class="{ 'flex-wrap': screenWidth < 374 }"
        >
          <div class="w-full md:w-auto">
            <div class="block text-sm text-body-color lg:text-base">Common</div>
            <div class="mt-3">
              <custom-badge
                :text="
                  nullTextify(
                    authority?.authorities?.common_authority_status,
                    'No Authority'
                  )
                "
                :size="screenSize === 'mobile' ? 'md' : 'lg'"
                :bg-color-class="
                  getStatusColor(
                    authority?.authorities?.common_authority_status
                  )
                "
                color-class="text-white"
              />
            </div>
          </div>
          <div class="w-full md:w-auto">
            <div class="block text-sm text-body-color lg:text-base">
              Contract
            </div>
            <div class="mt-3">
              <custom-badge
                :text="
                  nullTextify(
                    authority?.authorities?.contract_authority_status,
                    'No Authority'
                  )
                "
                :size="screenSize === 'mobile' ? 'md' : 'lg'"
                :bg-color-class="
                  getStatusColor(
                    authority?.authorities?.contract_authority_status
                  )
                "
                color-class="text-white"
              />
            </div>
          </div>
          <div class="w-full md:w-auto">
            <div class="block text-sm text-body-color lg:text-base">Broker</div>
            <div class="mt-3">
              <custom-badge
                :text="
                  nullTextify(
                    authority?.authorities?.broker_authority_status,
                    'No Authority'
                  )
                "
                :size="screenSize === 'mobile' ? 'md' : 'lg'"
                :bg-color-class="
                  getStatusColor(
                    authority?.authorities?.broker_authority_status
                  )
                "
                color-class="text-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!emptyElement(authority?.authorities)"
      class="mt-6 flex flex-col"
    >
      <h4 class="text-left text-lg text-dark-gray">Authority History</h4>
      <div class="mt-3">
        <table
          v-if="screenSize != 'mobile'"
          class="striped w-full table-auto"
        >
          <thead>
            <tr
              class="h-3 cursor-pointer select-none text-left text-xs text-dark-gray"
            >
              <th class="px-2 text-left font-light">Authority Type</th>
              <th class="px-2 font-light">Original Action</th>
              <th class="px-2 font-light">Action Date</th>
              <th class="px-2 font-light">Disposition Action</th>
              <th class="px-2 font-light">Disposition Date</th>
            </tr>
          </thead>
          <tbody class="rounded-md border border-light-gray">
            <tr
              v-for="(authorityHistory, idx) in authorityHistoryRecords"
              :key="authorityHistory?.id"
              class="h-14 cursor-pointer text-left text-xs text-body-color hover:bg-lighter-gray"
              :class="[idx % 2 === 0 ? 'bg-white' : 'bg-gray-50', 'py-2']"
            >
              <td class="pl-4 text-left">
                {{ authorityHistory?.authority_type }}
              </td>
              <td class="px-2">
                {{ authorityHistory?.original_action }}
              </td>
              <td class="px-2">
                {{ displayDate(authorityHistory?.original_action_date) }}
              </td>
              <td class="px-2">
                {{ authorityHistory?.disposition_action }}
              </td>
              <td class="px-2">
                {{ displayDate(authorityHistory?.disposition_date) }}
              </td>
            </tr>
          </tbody>
        </table>
        <section
          v-else
          class=""
        >
          <div
            v-for="(authorityHistory, idx) in authorityHistoryRecords"
            :key="idx"
            :class="{
              'rounded-t-md': idx === 0,
              'border-t-0': idx !== 0,
              'rounded-b-md': idx === authorityHistoryRecords.length - 1,
            }"
            :style="idx % 2 !== 0 ? 'background-color: #FCFCFF' : ''"
            class="border p-4 text-left"
          >
            <p class="mb-3 text-base">Contract Authority</p>
            <div class="grid grid-cols-2">
              <div class="p-2">
                <label class="text-sm text-dark-gray">Original Action</label>
                <p class="text-xs text-body-color">
                  {{ authorityHistory?.original_action }}
                </p>
              </div>
              <div class="p-2">
                <label class="text-sm text-dark-gray">Action Date</label>
                <p class="text-xs text-body-color">
                  {{ displayDate(authorityHistory?.original_action_date) }}
                </p>
              </div>
            </div>
            <div class="grid grid-cols-2">
              <div class="p-2">
                <label class="text-sm text-dark-gray">Disposition Action</label>
                <p class="text-xs text-body-color">
                  {{ authorityHistory?.disposition_action }}
                </p>
              </div>
              <div class="p-2">
                <label class="text-sm text-dark-gray">Disposition Date</label>
                <p class="text-xs text-body-color">
                  {{ displayDate(authorityHistory?.disposition_date) }}
                </p>
              </div>
            </div>
            <div class="grid grid-cols-2">
              <div class="p-2">
                <label class="text-sm text-dark-gray">
                  Continuous Authority
                </label>
                <p class="text-xs text-body-color">MM/DD/YY</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import CustomBadge from '@/components/CustomBadge.vue';
import moment from 'moment';
// import Pagination from "@/components/Pagination.vue";

export default {
  name: 'AuthoritySection',
  components: {
    CustomBadge,
    // Pagination,
  },
  props: {
    authority: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentSort: 'date',
      currentSortDir: 'asc',
      currentSortRates: 'date',
      currentSortDirRates: 'asc',
      historyColumns: [
        { label: 'Authority Type', key: 'authorityType', class: 'text-black' },
        { label: 'Original Action', key: 'originalAction' },
        { label: 'Action Date', key: 'actionDate' },
        { label: 'Disposition Action', key: 'dispositionAction' },
        { label: 'Disposition Date', key: 'dispositionDate' },
      ],
      rateColumns: [
        { label: 'Type', key: 'type' },
        { label: 'Inspections', key: 'inspections' },
        { label: 'OOS', key: 'oos' },
        { label: 'Carrier Avg', key: 'carrierAvg' },
        { label: 'National Avg', key: 'nationalAvg' },
      ],
      selectedPage: 1,
      perPage: 5,
      screenWidth: window.innerWidth,
      screenSize:
        window.innerWidth <= 640
          ? 'mobile'
          : window.innerWidth <= 1024
            ? 'tablet'
            : 'desktop',
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.authority?.histories?.length / this.perPage);
    },
    authorityHistoryRecords: function () {
      const start = (this.selectedPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.authority?.histories?.slice(start, end);
    },
    carrierStatusColor: function () {
      return this.getStatusColor(this.carrierProperties?.summary?.status);
    },
    commonStatusColor: function () {
      return this.getStatusColor(this.carrierProperties?.common?.status);
    },
    contractStatusColor: function () {
      return this.getStatusColor(this.carrierProperties?.contract?.status);
    },
    brokerStatusColor: function () {
      return this.getStatusColor(this.carrierProperties?.broker?.status);
    },
  },
  watch: {
    // sortedRecords: {
    //   deep: true,
    //   handler: function () {
    //     return this.recordsLocal.sort((a,b) => {
    //       let modifier = 1;
    //       if(this.currentSortDir === 'desc') modifier = -1;
    //       if(a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
    //       if(a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
    //       return 0;
    //     });
    //   }
    // },
    // sortedRates: {
    //   deep: true,
    //   handler: function () {
    //     return this.ratesLocal.sort((a,b) => {
    //       let modifier = 1;
    //       if(this.currentSortDirRates === 'desc') modifier = -1;
    //       if(a[this.currentSortRates] < b[this.currentSortRates]) return -1 * modifier;
    //       if(a[this.currentSortRates] > b[this.currentSortRates]) return 1 * modifier;
    //       return 0;
    //     });
    //   }
    // }
  },
  mounted() {
    addEventListener('resize', this.resizeListener);
  },
  beforeUnmount() {
    removeEventListener('resize', this.resizeListener);
  },
  methods: {
    resizeListener() {
      this.screenWidth = window.innerWidth;

      switch (true) {
        case window.innerWidth <= 640:
          this.screenSize = 'mobile';
          break;
        case window.innerWidth <= 1024:
          this.screenSize = 'tablet';
          break;
        default:
          this.screenSize = 'desktop';
      }
    },
    onSelectPage: function (newPage) {
      this.selectedPage = newPage;
    },
    getStatusColor: function (status) {
      switch (status) {
        case 'Active':
          return 'bg-green-500';
        case 'Inactive':
          return 'bg-red-500';
        case 'Pending':
          return 'bg-orange-500';
        default:
          return 'bg-body-color';
      }
    },
    displayDate: function (initialDate) {
      if (initialDate == null) {
        return initialDate;
      } else {
        return moment(initialDate, 'YYYY-MM-DD').format('M/D/YY');
      }
    },
    emptyElement: function (value) {
      if (value == null) {
        return true;
      } else if (typeof value === 'object') {
        if (Object.values(value).every((x) => x === null || x === '') == true) {
          return true;
        }
        if (Array.isArray(value)) {
          if (!value?.length) {
            return true;
          }
        }
      } else {
        return false;
      }
    },
    nullTextify: function (value, nullVal = 'N/A') {
      if (value == null) {
        return nullVal;
      } else {
        return value;
      }
    },
    // sort: function(s) {
    //   if(s === this.currentSort) {
    //     this.currentSortDir = this.currentSortDir==='asc'?'desc':'asc';
    //   }
    //   this.currentSort = s;
    // }
  },
};
</script>
<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
  background: white;
  border-radius: 10px;
}

thead {
  background: white;
  padding: 10px 10px 10px 0;
}

tbody {
  display: block;
  min-height: 100px;
  overflow-y: auto;
  height: calc(100% - 96px);
  border-radius: 5px;
  border: 1px solid #ededf0;
}

thead,
tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

table tr td {
  color: #6d7181;
  padding: 0 8px;
}

/* top-left border-radius */
table tr:first-child td:first-child {
  border-top-left-radius: 6px;
}

/* top-right border-radius */
table tr:first-child td:last-child {
  border-top-right-radius: 6px;
}

/* bottom-left border-radius */
table tr:last-child td:first-child {
  border-bottom-left-radius: 6px;
}

/* bottom-right border-radius */
table tr:last-child td:last-child {
  border-bottom-right-radius: 6px;
}
</style>
